import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, RotateCcw, CheckCircle, Mail, MessageCircle, RefreshCw, Shield, Loader2, Calendar, FileText, Download, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PatientPortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const sigCanvas = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: verificar doc, 2: leer consent, 3: verificar códigos, 4: firmar, 5: confirmar, 6: completado
  const [consentData, setConsentData] = useState(null);
  const [verifiedData, setVerifiedData] = useState(null);
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [verified, setVerified] = useState(false);
  const [verifyingDocument, setVerifyingDocument] = useState(false);
  const [hasReadConsent, setHasReadConsent] = useState(false);
  const [formData, setFormData] = useState({
    codigo_verificacion_email: '',
    codigo_verificacion_whatsapp: '',
    fecha_expedicion_documento: ''
  });
  const [signatureData, setSignatureData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [completedSerial, setCompletedSerial] = useState(null);
  const [notFoundMessage, setNotFoundMessage] = useState('Consentimiento no encontrado');
  
  // Verification state
  const [verificationStatus, setVerificationStatus] = useState({
    email: { sent: false, validated: false, sending: false },
    whatsapp: { sent: false, validated: false, sending: false }
  });
  const [emailCodeValidated, setEmailCodeValidated] = useState(false);
  const [whatsappCodeValidated, setWhatsappCodeValidated] = useState(false);
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [validatingWhatsapp, setValidatingWhatsapp] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [whatsappCooldown, setWhatsappCooldown] = useState(0);

  // Cooldown timer effect
  useEffect(() => {
    if (emailCooldown <= 0 && whatsappCooldown <= 0) return;
    const interval = setInterval(() => {
      setEmailCooldown(prev => Math.max(0, prev - 1));
      setWhatsappCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [emailCooldown > 0 || whatsappCooldown > 0]);

  useEffect(() => {
    fetchConsentData();
  }, [token]);

  const fetchConsentData = async () => {
    try {
      // Only confirms the link is usable (exists, not expired, still pending) —
      // the server deliberately withholds any patient data until the document
      // number is verified via handleVerifyDocument below.
      const response = await axios.get(`${API_URL}/consents/by-token/${token}`);
      setConsentData(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Consentimiento no encontrado';
      setNotFoundMessage(message);
      toast.error(message);
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/verification/status/${token}`);
      setVerificationStatus(prev => ({
        email: { ...prev.email, ...response.data.email },
        whatsapp: { ...prev.whatsapp, ...response.data.whatsapp }
      }));
      if (response.data.email?.validated) setEmailCodeValidated(true);
      if (response.data.whatsapp?.validated) setWhatsappCodeValidated(true);
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const handleVerifyDocument = async (e) => {
    e.preventDefault();
    setVerifyingDocument(true);
    try {
      // The document number is checked server-side — the client never receives
      // patient data (name, document, birth date, email, WhatsApp) until this
      // succeeds.
      const response = await axios.post(`${API_URL}/consents/verify-identity/${token}`, {
        numero_documento: numeroDocumento
      });
      setVerifiedData(response.data);
      if (response.data.verification_status?.both_verified) {
        setEmailCodeValidated(true);
        setWhatsappCodeValidated(true);
      }
      setVerified(true);
      setStep(2);
      toast.success('Documento verificado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Número de documento incorrecto');
    } finally {
      setVerifyingDocument(false);
    }
  };

  const handleContinueToVerification = () => {
    if (!hasReadConsent) {
      toast.error('Debe marcar que ha leído y comprendido el consentimiento');
      return;
    }
    setStep(3);
    fetchVerificationStatus();
  };

  const sendVerificationCode = async (codeType) => {
    setVerificationStatus(prev => ({
      ...prev,
      [codeType]: { ...prev[codeType], sending: true }
    }));

    try {
      const response = await axios.post(`${API_URL}/verification/send-code`, {
        consent_token: token,
        code_type: codeType
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setVerificationStatus(prev => ({
          ...prev,
          [codeType]: { 
            ...prev[codeType], 
            sent: true, 
            sending: false
          }
        }));
        // Start 5-minute cooldown (300 seconds)
        if (codeType === 'email') setEmailCooldown(300);
        else setWhatsappCooldown(300);
      } else {
        toast.error(response.data.message || 'Error al enviar código');
        setVerificationStatus(prev => ({
          ...prev,
          [codeType]: { ...prev[codeType], sending: false }
        }));
      }
    } catch (error) {
      toast.error('Error al enviar código');
      setVerificationStatus(prev => ({
        ...prev,
        [codeType]: { ...prev[codeType], sending: false }
      }));
    }
  };

  const validateSingleCode = async (codeType) => {
    const code = codeType === 'email' ? formData.codigo_verificacion_email : formData.codigo_verificacion_whatsapp;
    
    if (!code) {
      toast.error(`Ingrese el código de ${codeType === 'email' ? 'correo' : 'WhatsApp'}`);
      return;
    }

    const setValidating = codeType === 'email' ? setValidatingEmail : setValidatingWhatsapp;
    setValidating(true);

    try {
      // Validate single code
      const response = await axios.post(`${API_URL}/verification/validate-codes`, {
        consent_token: token,
        email_code: codeType === 'email' ? code : formData.codigo_verificacion_email || '000000',
        whatsapp_code: codeType === 'whatsapp' ? code : formData.codigo_verificacion_whatsapp || '00000'
      });

      // Check specific validation
      await fetchVerificationStatus();
      
      const status = await axios.get(`${API_URL}/verification/status/${token}`);
      
      if (codeType === 'email' && status.data.email?.validated) {
        setEmailCodeValidated(true);
        toast.success('Código de correo validado');
      } else if (codeType === 'whatsapp' && status.data.whatsapp?.validated) {
        setWhatsappCodeValidated(true);
        toast.success('Código de WhatsApp validado');
      } else if (response.data.errors) {
        const relevantError = response.data.errors.find(e => 
          codeType === 'email' ? e.includes('email') : e.includes('WhatsApp')
        );
        if (relevantError) {
          toast.error(relevantError);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al validar código');
    } finally {
      setValidating(false);
    }
  };

  const handleContinueToSign = () => {
    if (!emailCodeValidated || !whatsappCodeValidated) {
      toast.error('Debe validar ambos códigos antes de continuar');
      return;
    }
    if (!formData.fecha_expedicion_documento) {
      toast.error('Ingrese la fecha de expedición del documento');
      return;
    }
    setStep(4);
  };

  const clearSignature = () => {
    sigCanvas.current.clear();
    setSignatureData(null);
  };

  const approveSignature = () => {
    if (sigCanvas.current.isEmpty()) {
      toast.error('Por favor, firma antes de continuar');
      return;
    }
    const dataURL = sigCanvas.current.toDataURL();
    setSignatureData(dataURL);
    setStep(5);
  };

  const handleSubmit = async (approved) => {
    if (approved && !signatureData) {
      toast.error('Firma requerida');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/consents/submit-signature/${token}`, {
        numero_documento: numeroDocumento,
        firma_base64: signatureData || '',
        codigo_verificacion_email: formData.codigo_verificacion_email,
        codigo_verificacion_adicional: formData.codigo_verificacion_whatsapp,
        fecha_expedicion_documento: formData.fecha_expedicion_documento,
        fecha_vencimiento_documento: '',
        approved: approved
      });

      if (approved) {
        setCompletedSerial(response.data.serial);
        setStep(6);
        toast.success('Consentimiento firmado exitosamente');
      } else {
        toast.info('Consentimiento rechazado');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar consentimiento');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    // Open the tab synchronously (on the click itself) so popup blockers don't
    // treat it as an unrequested popup once the async request resolves; the
    // document number travels in the request body, never in the URL.
    const pdfWindow = window.open('', '_blank');
    try {
      const response = await axios.post(
        `${API_URL}/consents/download-pdf/${completedSerial}`,
        { numero_documento: numeroDocumento },
        { responseType: 'blob' }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      if (pdfWindow) pdfWindow.location.href = blobUrl;
    } catch (error) {
      if (pdfWindow) pdfWindow.close();
      toast.error('Error al descargar el PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-slate-600 text-sm sm:text-base">Cargando consentimiento...</p>
        </div>
      </div>
    );
  }

  if (!consentData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-red-600 font-semibold">{notFoundMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-portal">
      <div className="max-w-4xl mx-auto p-3 sm:p-4 md:p-8">
        <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-indigo-700 px-4 sm:px-8 py-4 sm:py-6 text-white">
            <h1 className="text-xl sm:text-2xl font-bold text-center" data-testid="portal-title">Konemed</h1>
            <p className="text-center text-violet-100 mt-1 text-sm sm:text-base">Firma de Consentimiento Informado</p>
          </div>

          {/* Step 6: Completado */}
          {step === 6 ? (
            <div className="p-4 sm:p-8 text-center" data-testid="completion-screen">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <CheckCircle className="text-emerald-600" size={40} />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">¡Consentimiento Firmado!</h2>
              <p className="text-slate-600 mb-2 text-sm sm:text-base">Tu consentimiento ha sido procesado exitosamente.</p>
              <p className="text-xs sm:text-sm text-slate-500 mb-6">Serial: <span className="font-mono font-semibold">{completedSerial}</span></p>
              
              <button
                onClick={handleDownload}
                data-testid="download-consent-button"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-all shadow-lg mx-auto"
              >
                <Download size={20} />
                Descargar Consentimiento
              </button>

              <p className="text-xs text-slate-500 mt-6">
                Una copia también ha sido enviada a tu correo electrónico y al centro médico.
              </p>
            </div>
          ) : (
            <div className="p-4 sm:p-8">
              {/* Progress Bar */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center justify-between mb-2 text-xs sm:text-sm">
                  <span className={`font-medium ${step >= 1 ? 'text-violet-600' : 'text-slate-400'}`}>Verificar</span>
                  <span className={`font-medium ${step >= 2 ? 'text-violet-600' : 'text-slate-400'}`}>Leer</span>
                  <span className={`font-medium ${step >= 3 ? 'text-violet-600' : 'text-slate-400'}`}>Códigos</span>
                  <span className={`font-medium ${step >= 4 ? 'text-violet-600' : 'text-slate-400'}`}>Firmar</span>
                  <span className={`font-medium ${step >= 5 ? 'text-violet-600' : 'text-slate-400'}`}>Confirmar</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-violet-600 to-indigo-600 transition-all duration-500"
                    style={{ width: `${(step / 5) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Step 1: Verify Document */}
              {step === 1 && (
                <div data-testid="step-verify-document">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">Verificación de Identidad</h2>
                  <p className="text-slate-600 mb-6 text-sm sm:text-base">
                    Para acceder al consentimiento, ingresa tu número de documento:
                  </p>
                  
                  <form onSubmit={handleVerifyDocument} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Número de Documento
                      </label>
                      <input
                        type="text"
                        value={numeroDocumento}
                        onChange={(e) => setNumeroDocumento(e.target.value)}
                        data-testid="input-verify-documento"
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50 text-base"
                        placeholder="Ingresa tu documento"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={verifyingDocument}
                      data-testid="verify-button"
                      className="w-full py-3 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white font-semibold rounded-lg transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      {verifyingDocument && <Loader2 className="animate-spin" size={18} />}
                      {verifyingDocument ? 'Verificando...' : 'Verificar y Continuar'}
                    </button>
                  </form>
                </div>
              )}

              {/* Step 2: Read Consent */}
              {step === 2 && verifiedData && (
                <div data-testid="step-read-consent">
                  <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-4 text-center">
                    {verifiedData.template.nombre_procedimiento}
                  </h2>

                  {/* Consent Content */}
                  <div className="max-h-[420px] sm:max-h-[520px] overflow-y-auto mb-6 p-4 sm:p-6 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="mb-6">
                      <h3 className="font-bold text-slate-900 mb-2 text-sm sm:text-base">Centro de Atención:</h3>
                      <p className="text-xs sm:text-sm text-slate-700"><strong>Razón Social:</strong> {verifiedData.company.razon_social}</p>
                      <p className="text-xs sm:text-sm text-slate-700"><strong>Documento:</strong> {verifiedData.company.numero_documento}</p>
                      <p className="text-xs sm:text-sm text-slate-700"><strong>Dirección:</strong> {verifiedData.company.direccion}</p>
                      {verifiedData.nombre_profesional && (
                        <p className="text-xs sm:text-sm text-slate-700"><strong>Profesional:</strong> {verifiedData.nombre_profesional}</p>
                      )}
                    </div>

                    <div className="mb-6">
                      <h3 className="font-bold text-slate-900 mb-2 text-sm sm:text-base">Políticas del Centro:</h3>
                      <p className="text-xs sm:text-sm text-slate-700 text-justify whitespace-pre-wrap">
                        {verifiedData.template.politicas_centro}
                      </p>
                    </div>

                    <div className="mb-6">
                      <h3 className="font-bold text-slate-900 mb-2 text-sm sm:text-base">Consentimiento del Procedimiento:</h3>
                      <p className="text-xs sm:text-sm text-slate-700 text-justify whitespace-pre-wrap">
                        {verifiedData.template.politicas_procedimiento}
                      </p>
                    </div>
                  </div>

                  {/* Checkbox "He leído y comprendido" */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hasReadConsent}
                        onChange={(e) => setHasReadConsent(e.target.checked)}
                        data-testid="checkbox-read-consent"
                        className="w-5 h-5 mt-0.5 text-violet-600 border-slate-300 rounded focus:ring-violet-500"
                      />
                      <span className="text-sm text-amber-800">
                        <strong>He leído y comprendido</strong> el contenido de este consentimiento informado.
                      </span>
                    </label>
                  </div>

                  <button
                    onClick={handleContinueToVerification}
                    data-testid="continue-to-verification"
                    className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-all shadow-lg"
                  >
                    Continuar a Verificación
                  </button>
                </div>
              )}

              {/* Step 3: Verification Codes */}
              {step === 3 && (
                <div data-testid="step-verification-codes">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="text-violet-600" size={24} />
                    <h2 className="text-lg sm:text-2xl font-bold text-slate-900">Verificación de Identidad</h2>
                  </div>
                  <p className="text-sm text-slate-600 mb-6">
                    Para garantizar la seguridad de tu firma, debes verificar tu identidad con códigos enviados a tu correo y WhatsApp.
                  </p>

                  {/* Email Verification - 6 dígitos */}
                  <div className="bg-white rounded-lg p-4 mb-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className={`${emailCodeValidated ? 'text-emerald-500' : 'text-violet-600'}`} size={20} />
                      <span className="font-medium text-slate-800 text-sm sm:text-base">Código por Correo (6 dígitos)</span>
                      {emailCodeValidated && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Validado</span>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <input
                        type="text"
                        maxLength="6"
                        value={formData.codigo_verificacion_email}
                        onChange={(e) => setFormData({ ...formData, codigo_verificacion_email: e.target.value.replace(/\D/g, '') })}
                        data-testid="input-codigo-email"
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white font-mono text-lg tracking-widest text-center"
                        placeholder="000000"
                        disabled={emailCodeValidated}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => sendVerificationCode('email')}
                          disabled={verificationStatus.email.sending || emailCodeValidated || emailCooldown > 0}
                          data-testid="send-email-code-button"
                          className="py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          {verificationStatus.email.sending ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : emailCooldown > 0 ? (
                            <RefreshCw size={18} />
                          ) : verificationStatus.email.sent ? (
                            <RefreshCw size={18} />
                          ) : (
                            <Mail size={18} />
                          )}
                          {emailCooldown > 0
                            ? `Reenviar (${Math.floor(emailCooldown / 60)}:${String(emailCooldown % 60).padStart(2, '0')})`
                            : verificationStatus.email.sent ? 'Reenviar' : 'Enviar código'}
                        </button>
                        <button
                          onClick={() => validateSingleCode('email')}
                          disabled={validatingEmail || emailCodeValidated || !formData.codigo_verificacion_email}
                          data-testid="validate-email-code-button"
                          className="py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          {validatingEmail ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                          Validar código
                        </button>
                      </div>
                      {verificationStatus.email.sent && !emailCodeValidated && (
                        <p className="text-xs text-emerald-600 text-center">
                          Código enviado al correo, revise en spam
                        </p>
                      )}
                    </div>
                  </div>

                  {/* WhatsApp Verification - 5 dígitos */}
                  <div className="bg-white rounded-lg p-4 mb-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageCircle className={`${whatsappCodeValidated ? 'text-emerald-500' : 'text-green-600'}`} size={20} />
                      <span className="font-medium text-slate-800 text-sm sm:text-base">Código por WhatsApp (5 dígitos)</span>
                      {whatsappCodeValidated && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Validado</span>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <input
                        type="text"
                        maxLength="5"
                        value={formData.codigo_verificacion_whatsapp}
                        onChange={(e) => setFormData({ ...formData, codigo_verificacion_whatsapp: e.target.value.replace(/\D/g, '') })}
                        data-testid="input-codigo-whatsapp"
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white font-mono text-lg tracking-widest text-center"
                        placeholder="00000"
                        disabled={whatsappCodeValidated}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => sendVerificationCode('whatsapp')}
                          disabled={verificationStatus.whatsapp.sending || whatsappCodeValidated || whatsappCooldown > 0}
                          data-testid="send-whatsapp-code-button"
                          className="py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          {verificationStatus.whatsapp.sending ? (
                            <Loader2 className="animate-spin" size={18} />
                          ) : whatsappCooldown > 0 ? (
                            <RefreshCw size={18} />
                          ) : verificationStatus.whatsapp.sent ? (
                            <RefreshCw size={18} />
                          ) : (
                            <MessageCircle size={18} />
                          )}
                          {whatsappCooldown > 0
                            ? `Reenviar (${Math.floor(whatsappCooldown / 60)}:${String(whatsappCooldown % 60).padStart(2, '0')})`
                            : verificationStatus.whatsapp.sent ? 'Reenviar' : 'Enviar código'}
                        </button>
                        <button
                          onClick={() => validateSingleCode('whatsapp')}
                          disabled={validatingWhatsapp || whatsappCodeValidated || !formData.codigo_verificacion_whatsapp}
                          data-testid="validate-whatsapp-code-button"
                          className="py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          {validatingWhatsapp ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                          Validar código
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Fecha de Expedición */}
                  <div className="bg-white rounded-lg p-4 mb-6 border border-slate-200">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                      <Calendar size={18} className="text-violet-600" />
                      Fecha de Expedición/Emisión del Documento <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.fecha_expedicion_documento}
                      onChange={(e) => setFormData({ ...formData, fecha_expedicion_documento: e.target.value })}
                      data-testid="input-fecha-expedicion"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white text-base"
                      required
                    />
                  </div>

                  {/* Status summary */}
                  {emailCodeValidated && whatsappCodeValidated && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4 text-center">
                      <div className="flex items-center justify-center gap-2 text-emerald-700">
                        <CheckCircle size={24} />
                        <span className="font-semibold">Códigos verificados correctamente</span>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleContinueToSign}
                    disabled={!emailCodeValidated || !whatsappCodeValidated || !formData.fecha_expedicion_documento}
                    data-testid="continue-to-sign"
                    className="w-full py-3 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-semibold rounded-lg transition-all shadow-lg"
                  >
                    Pasar a Firmar
                  </button>
                </div>
              )}

              {/* Step 4: Signature */}
              {step === 4 && (
                <div data-testid="step-signature">
                  <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-4 text-center">Firma Digital</h2>
                  <p className="text-sm text-slate-600 mb-4 text-center">
                    Dibuja tu firma en el recuadro siguiente
                  </p>
                  
                  <div className="mb-4">
                    <div className="border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-white" data-testid="signature-canvas-container">
                      <SignatureCanvas
                        ref={sigCanvas}
                        canvasProps={{
                          className: 'signature-canvas w-full',
                          style: { height: '200px', width: '100%' },
                          'data-testid': 'signature-canvas'
                        }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 text-center mt-2">Firma aquí con tu dedo o mouse</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={clearSignature}
                      data-testid="clear-signature-button"
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors text-sm sm:text-base"
                    >
                      <RotateCcw size={18} />
                      Repetir Firma
                    </button>
                    <button
                      type="button"
                      onClick={approveSignature}
                      data-testid="approve-signature-button"
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors shadow-lg text-sm sm:text-base"
                    >
                      <Check size={18} />
                      Aprobar Firma
                    </button>
                  </div>
                </div>
              )}

              {/* Step 5: Final Confirmation */}
              {step === 5 && (
                <div data-testid="step-final-confirmation">
                  <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-4 text-center">Confirmación Final</h2>
                  
                  <div className="bg-slate-50 rounded-lg p-4 sm:p-6 mb-6">
                    <h3 className="font-semibold text-slate-900 mb-3 text-sm sm:text-base">Vista Previa de Firma:</h3>
                    {signatureData && (
                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <img src={signatureData} alt="Firma" className="max-h-24 sm:max-h-32 mx-auto" data-testid="signature-preview" />
                      </div>
                    )}
                  </div>

                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 sm:p-4 mb-4">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle size={20} />
                      <span className="font-medium text-sm sm:text-base">Identidad verificada con códigos de Correo y WhatsApp</span>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 mb-6">
                    <p className="text-xs sm:text-sm text-amber-800">
                      Al aprobar, confirmas que has leído, comprendido y aceptas el consentimiento informado.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      onClick={() => handleSubmit(false)}
                      disabled={submitting}
                      data-testid="reject-button"
                      className="flex items-center justify-center gap-2 px-6 py-4 bg-slate-400 hover:bg-slate-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50 order-2 sm:order-1"
                    >
                      <X size={20} />
                      Rechazar Consentimiento
                    </button>
                    <button
                      onClick={() => handleSubmit(true)}
                      disabled={submitting}
                      data-testid="approve-button"
                      className="flex items-center justify-center gap-2 px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 shadow-lg order-1 sm:order-2"
                    >
                      <Check size={20} />
                      {submitting ? 'Procesando...' : 'Aprobar Consentimiento'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <p className="text-xs sm:text-sm text-slate-500">Powered by <span className="font-semibold">Konemed</span></p>
        </div>
      </div>
    </div>
  );
}
